import { signal, computed } from '@preact/signals';
import type { Contact, WhatsAppContactSync } from '../types/contact';
import type { Message, SummaryJob } from '../types/message';
import type { ActionItem, ExecutiveSummary, ProjectTopic, AiChatMessage } from '../types/ai';
import { apiFetch } from '../services/api';
import { showToast, activeTabSignal, highlightedMessageIdSignal, isNavigatingToMessageSignal, navigationOverlayTextSignal } from './ui.store';
import { normalizeText } from '../utils/formatters';
import {
  messagesSignal,
  hasMoreLocalMessagesSignal,
  isLoadingOlderMessagesSignal,
  noMoreCloudMessagesSignal,
  isSyncingHistorySignal
} from './messages.store';
import {
  actionsSignal,
  summariesSignal,
  projectsSignal,
  aiChatHistorySignal,
  smartRepliesSignal,
  isGeneratingSmartRepliesSignal,
  pendingNewMessagesCountSignal,
  daysSinceLastSummarySignal,
  isStaleSummarySignal
} from './ai.store';

export interface CachedContactDetails {
  messages: Message[];
  actions: ActionItem[];
  summaries: ExecutiveSummary[];
  projects: ProjectTopic[];
  chatHistory: AiChatMessage[];
}

export const CACHED_CONTACTS_KEY = 'alfred_cached_contacts';

export function getInitialContacts(): Contact[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(CACHED_CONTACTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveContactsToCache(contacts: Contact[]) {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(CACHED_CONTACTS_KEY, JSON.stringify(contacts));
    } catch (err) {
      console.warn('[ContactsStore] Falha ao gravar cache local de contatos:', err);
    }
  }
}

export const contactsSignal = signal<Contact[]>(getInitialContacts());
export const isLoadingContactsSignal = signal<boolean>(getInitialContacts().length === 0);
export const contactsErrorSignal = signal<string | null>(null);
export const waContactsSignal = signal<WhatsAppContactSync[]>([]);
export const activeContactSignal = signal<Contact | null>(null);
export const contactSearchSignal = signal<string>('');
export const activeWaFilterSignal = signal<'contacts' | 'groups'>('contacts');
export const isCancellingSummarySignal = signal<boolean>(false);
export const activeSummaryJobSignal = signal<SummaryJob | null>(null);
export const syncingContactsSignal = signal<Record<string, boolean>>({});
export const syncHistoryProgressSignal = signal<Record<string, { progress: number; totalSynced: number; audiosTranscribed: number }>>({});
export const isLoadingContactDetailsSignal = signal<boolean>(false);
export const isLoadingActionsSignal = signal<boolean>(false);
export const isLoadingSummariesSignal = signal<boolean>(false);
export const isLoadingProjectsSignal = signal<boolean>(false);
export const isLoadingMessagesSignal = signal<boolean>(false);

export const contactDetailsCache = new Map<string, CachedContactDetails>();

export const sortedContacts = computed(() => {
  const normQuery = normalizeText(contactSearchSignal.value);
  const words = normQuery.split(/\s+/).filter(Boolean);
  let list = contactsSignal.value;

  if (words.length > 0) {
    list = list.filter(c => {
      const nameNorm = normalizeText(c.name || '');
      const phoneNorm = (c.phone_number || '').replace(/\D/g, '');

      return words.every(w => {
        const isNumeric = /^\d+$/.test(w);
        if (isNumeric) {
          return phoneNorm.includes(w);
        }
        return nameNorm.includes(w);
      });
    });
  }

  // Ordenar pelos grupos mais recentes quando a busca estiver vazia
  return [...list].sort((a, b) => {
    if (words.length === 0) {
      const aIsGroup = Boolean(a.is_group || (a.jid && a.jid.includes('@g.us')));
      const bIsGroup = Boolean(b.is_group || (b.jid && b.jid.includes('@g.us')));
      if (aIsGroup && !bIsGroup) return -1;
      if (!aIsGroup && bIsGroup) return 1;
    }
    const tA = a.last_message_timestamp ? new Date(a.last_message_timestamp).getTime() : 0;
    const tB = b.last_message_timestamp ? new Date(b.last_message_timestamp).getTime() : 0;
    return tB - tA;
  });
});

import { parseUrlState, syncUrlWithState } from '../services/router';

export async function loadContacts() {
  if (contactsSignal.value.length === 0) {
    isLoadingContactsSignal.value = true;
  }
  contactsErrorSignal.value = null;

  try {
    // Carregamento paralelo da lista de contatos e dos jobs de sincronização
    const [contacts] = await Promise.all([
      apiFetch<Contact[]>('/contacts'),
      loadActiveSyncJobs()
    ]);

    contactsSignal.value = contacts;
    isLoadingContactsSignal.value = false;
    saveContactsToCache(contacts);

    const { phone: urlPhone, tab: urlTab } = parseUrlState();
    if (urlPhone) {
      const target = contacts.find(c => {
        const cClean = (c.phone_number || '').replace(/\D/g, '');
        return cClean === urlPhone || c.phone_number === urlPhone || c.jid === urlPhone;
      });
      if (target) {
        activeTabSignal.value = urlTab;
        selectContact(target, { skipUrlSync: true });
        syncUrlWithState({ replace: true });
        return;
      }
    }

    if (contacts.length > 0 && !activeContactSignal.value) {
      // No desktop, pré-seleciona o primeiro contato para experiência instantânea
      if (typeof window !== 'undefined' && window.innerWidth >= 768) {
        selectContact(contacts[0]);
      }
    }
  } catch (err: any) {
    console.error('[ContactsStore] Erro ao carregar contatos:', err);
    isLoadingContactsSignal.value = false;
    if (contactsSignal.value.length === 0) {
      contactsErrorSignal.value = err.message || 'Erro ao carregar contatos.';
    }
  }
}

export async function loadActiveSyncJobs() {
  try {
    const res = await apiFetch<{ jobs: { contactPhone: string; progress?: number; totalSynced?: number; audiosTranscribed?: number }[] }>('/contacts/sync-history/active-jobs');
    const nextSyncing: Record<string, boolean> = {};
    const nextProgress: Record<string, { progress: number; totalSynced: number; audiosTranscribed: number }> = {};

    if (res && Array.isArray(res.jobs)) {
      for (const job of res.jobs) {
        if (job.contactPhone) {
          nextSyncing[job.contactPhone] = true;
          nextProgress[job.contactPhone] = {
            progress: job.progress || 5,
            totalSynced: job.totalSynced || 0,
            audiosTranscribed: job.audiosTranscribed || 0
          };
        }
      }
    }
    syncingContactsSignal.value = nextSyncing;
    syncHistoryProgressSignal.value = nextProgress;
  } catch (err) {
    console.error('[ContactsStore] Erro ao carregar jobs de sincronização ativos:', err);
  }
}

export async function triggerSyncHistory(phone: string) {
  const clean = phone.replace(/\D/g, '');
  if (!clean) return;

  if (syncingContactsSignal.value[clean]) {
    showToast('Já existe uma sincronização de histórico em andamento para este contato.', 'warning');
    return;
  }

  syncingContactsSignal.value = { ...syncingContactsSignal.value, [clean]: true };

  try {
    const res = await apiFetch<{ message: string }>(`/contacts/${clean}/sync-history`, {
      method: 'POST'
    });
    showToast(res.message || 'Sincronização de histórico iniciada em segundo plano!', 'success');
  } catch (err: any) {
    if (err.status === 409) {
      showToast('Já existe uma sincronização de histórico em andamento para este contato.', 'warning');
    } else {
      const updated = { ...syncingContactsSignal.value };
      delete updated[clean];
      syncingContactsSignal.value = updated;
      showToast(`Erro ao iniciar sincronização: ${err.message}`, 'error');
    }
  }
}

export async function selectContact(contact: Contact | null, options?: { skipUrlSync?: boolean }) {
  if (!contact) return;

  // Descartar respostas/sugestões inteligentes geradas ao trocar de conversa
  smartRepliesSignal.value = [];
  isGeneratingSmartRepliesSignal.value = false;

  activeContactSignal.value = contact;
  noMoreCloudMessagesSignal.value = false;
  isSyncingHistorySignal.value = false;

  if (!options?.skipUrlSync) {
    syncUrlWithState();
  }

  const phone = contact.phone_number;
  const cached = contactDetailsCache.get(phone);

  if (cached) {
    messagesSignal.value = cached.messages;
    actionsSignal.value = cached.actions;
    summariesSignal.value = cached.summaries;
    projectsSignal.value = cached.projects;
    aiChatHistorySignal.value = cached.chatHistory;
  } else {
    messagesSignal.value = [];
    actionsSignal.value = [];
    summariesSignal.value = [];
    projectsSignal.value = [];
    aiChatHistorySignal.value = [];
  }

  // Sempre ativas os sinais de carregamento ao alternar conversas para mostrar os skeletons
  const currentTab = activeTabSignal.value;
  if (currentTab === 'tab-actions' || currentTab === 'tab-summaries') {
    isLoadingActionsSignal.value = true;
    isLoadingSummariesSignal.value = true;
    isLoadingProjectsSignal.value = true;
    isLoadingMessagesSignal.value = cached ? false : true;
  } else {
    isLoadingMessagesSignal.value = true;
    isLoadingActionsSignal.value = true;
    isLoadingSummariesSignal.value = true;
    isLoadingProjectsSignal.value = true;
  }
  isLoadingContactDetailsSignal.value = true;

  await loadContactDetails(contact);
}

export async function ensureMessagesLoaded(contact: Contact | null) {
  if (!contact) return;
  const phone = contact.phone_number;
  if (messagesSignal.value.length === 0 && !isLoadingMessagesSignal.value) {
    isLoadingMessagesSignal.value = true;
    try {
      const messages = await apiFetch<Message[]>(`/messages/${phone}`);
      if (activeContactSignal.value?.phone_number === phone) {
        messagesSignal.value = messages;
        hasMoreLocalMessagesSignal.value = (messages.length >= 100);
        isLoadingOlderMessagesSignal.value = false;
      }
    } catch (err) {
      console.error('[ContactsStore] Erro ao carregar mensagens sob demanda:', err);
    } finally {
      isLoadingMessagesSignal.value = false;
    }
  }
}

export async function loadContactDetails(contact: Contact) {
  if (!contact) return;
  const phone = contact.phone_number;
  const currentTab = activeTabSignal.value;

  try {
    if (currentTab === 'tab-actions' || currentTab === 'tab-summaries') {
      // PRIORIDADE 1: Carregar imediatamente Ações, Resumos, Projetos e Chat IA
      const [actions, summariesRes, projects, chatHistory, jobRes] = await Promise.all([
        apiFetch<ActionItem[]>(`/ai/actions/${phone}`),
        apiFetch<any>(`/ai/summaries/${phone}`),
        apiFetch<ProjectTopic[]>(`/ai/projects/${phone}`),
        apiFetch<AiChatMessage[]>(`/ai/chat-history/${phone}`),
        apiFetch<{ job?: SummaryJob | null }>(`/ai/summarize/${phone}/job`).catch(() => ({ job: null }))
      ]);

      if (activeContactSignal.value?.phone_number === phone) {
        actionsSignal.value = actions;
        const summaryList = Array.isArray(summariesRes) ? summariesRes : (summariesRes?.summaries || []);
        summariesSignal.value = summaryList;

        if (!Array.isArray(summariesRes)) {
          pendingNewMessagesCountSignal.value = summariesRes?.pendingNewMessagesCount || 0;
          daysSinceLastSummarySignal.value = summariesRes?.daysSinceLastSummary || 0;
          isStaleSummarySignal.value = Boolean(summariesRes?.isStaleSummary);
        } else {
          pendingNewMessagesCountSignal.value = 0;
          daysSinceLastSummarySignal.value = 0;
          isStaleSummarySignal.value = false;
        }

        projectsSignal.value = projects;
        activeSummaryJobSignal.value = (jobRes?.job && ['TRANSCRIBING', 'GENERATING_SUMMARY', 'EXTRACTING_ACTIONS'].includes(jobRes.job.status)) ? jobRes.job : null;

        isLoadingActionsSignal.value = false;
        isLoadingSummariesSignal.value = false;
        isLoadingProjectsSignal.value = false;
      }

      // PRIORIDADE 2: Carregar Mensagens & Transcrições em segundo plano
      isLoadingMessagesSignal.value = true;
      const messages = await apiFetch<Message[]>(`/messages/${phone}`);
      if (activeContactSignal.value?.phone_number === phone) {
        messagesSignal.value = messages;
        hasMoreLocalMessagesSignal.value = (messages.length >= 100);
        isLoadingOlderMessagesSignal.value = false;
        isLoadingMessagesSignal.value = false;

        contactDetailsCache.set(phone, { messages, actions, summaries: summaryList, projects, chatHistory });
      }
    } else {
      // Aba Mensagens Ativa: Carregar Mensagens com prioridade máxima
      isLoadingMessagesSignal.value = true;

      const messagesPromise = apiFetch<Message[]>(`/messages/${phone}`);
      const aiPromise = Promise.all([
        apiFetch<ActionItem[]>(`/ai/actions/${phone}`),
        apiFetch<any>(`/ai/summaries/${phone}`),
        apiFetch<ProjectTopic[]>(`/ai/projects/${phone}`),
        apiFetch<AiChatMessage[]>(`/ai/chat-history/${phone}`),
        apiFetch<{ job?: SummaryJob | null }>(`/ai/summarize/${phone}/job`).catch(() => ({ job: null }))
      ]);

      const messages = await messagesPromise;
      if (activeContactSignal.value?.phone_number === phone) {
        messagesSignal.value = messages;
        hasMoreLocalMessagesSignal.value = (messages.length >= 100);
        isLoadingOlderMessagesSignal.value = false;
        isLoadingMessagesSignal.value = false;
      }

      const [actions, summariesRes, projects, chatHistory, jobRes] = await aiPromise;
      if (activeContactSignal.value?.phone_number === phone) {
        actionsSignal.value = actions;
        const summaryList = Array.isArray(summariesRes) ? summariesRes : (summariesRes?.summaries || []);
        summariesSignal.value = summaryList;

        if (!Array.isArray(summariesRes)) {
          pendingNewMessagesCountSignal.value = summariesRes?.pendingNewMessagesCount || 0;
          daysSinceLastSummarySignal.value = summariesRes?.daysSinceLastSummary || 0;
          isStaleSummarySignal.value = Boolean(summariesRes?.isStaleSummary);
        } else {
          pendingNewMessagesCountSignal.value = 0;
          daysSinceLastSummarySignal.value = 0;
          isStaleSummarySignal.value = false;
        }

        projectsSignal.value = projects;
        activeSummaryJobSignal.value = (jobRes?.job && ['TRANSCRIBING', 'GENERATING_SUMMARY', 'EXTRACTING_ACTIONS'].includes(jobRes.job.status)) ? jobRes.job : null;

        isLoadingActionsSignal.value = false;
        isLoadingSummariesSignal.value = false;
        isLoadingProjectsSignal.value = false;

        contactDetailsCache.set(phone, { messages, actions, summaries: summaryList, projects, chatHistory });
      }
    }
  } catch (err) {
    console.error('[ContactsStore] Erro ao carregar detalhes do contato:', err);
  } finally {
    if (activeContactSignal.value?.phone_number === phone) {
      isLoadingContactDetailsSignal.value = false;
      isLoadingActionsSignal.value = false;
      isLoadingSummariesSignal.value = false;
      isLoadingProjectsSignal.value = false;
      isLoadingMessagesSignal.value = false;
    }
  }
}

export async function deleteContact(contact: Contact): Promise<boolean> {
  try {
    await apiFetch(`/contacts/${contact.phone_number}`, { method: 'DELETE' });
    contactDetailsCache.delete(contact.phone_number);
    contactsSignal.value = contactsSignal.value.filter(c => c.phone_number !== contact.phone_number);
    saveContactsToCache(contactsSignal.value);

    if (activeContactSignal.value?.phone_number === contact.phone_number) {
      const remaining = contactsSignal.value;
      if (remaining.length > 0) {
        selectContact(remaining[0]);
      } else {
        activeContactSignal.value = null;
        messagesSignal.value = [];
        actionsSignal.value = [];
        summariesSignal.value = [];
        projectsSignal.value = [];
        aiChatHistorySignal.value = [];
      }
    }
    showToast(`Conversa de "${contact.name}" excluída com sucesso!`, 'success');
    return true;
  } catch (err: any) {
    console.error('[ContactsStore] Erro ao excluir contato:', err);
    showToast(`Erro ao excluir conversa: ${err.message || 'Falha na requisição'}`, 'error');
    return false;
  }
}

export async function updateContactName(contactId: number, newName: string, notes?: string): Promise<boolean> {
  try {
    await apiFetch(`/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify({ name: newName, notes })
    });

    contactsSignal.value = contactsSignal.value.map(c => {
      if (c.id === contactId) {
        const updated = { ...c, name: newName, notes: notes !== undefined ? notes : c.notes };
        if (activeContactSignal.value?.id === contactId) {
          activeContactSignal.value = updated;
        }
        return updated;
      }
      return c;
    });
    saveContactsToCache(contactsSignal.value);

    showToast('Nome do contato atualizado com sucesso!', 'success');
    return true;
  } catch (err: any) {
    console.error('[ContactsStore] Erro ao atualizar contato:', err);
    showToast(`Erro ao atualizar nome: ${err.message || 'Falha na requisição'}`, 'error');
    return false;
  }
}

export async function navigateToSourceMessage(contactPhone: string, messageId?: string | null) {
  const cleanPhone = (contactPhone || '').replace(/\D/g, '');

  navigationOverlayTextSignal.value = 'Localizando mensagem de origem e preparando o chat...';
  isNavigatingToMessageSignal.value = true;

  try {
    const contacts = contactsSignal.value;
    const targetContact = contacts.find(c => {
      const cClean = (c.phone_number || '').replace(/\D/g, '');
      return cClean === cleanPhone || c.phone_number === contactPhone || c.jid === contactPhone;
    });

    if (targetContact) {
      await selectContact(targetContact);
    }

    if (messageId) {
      const currentMsgs = messagesSignal.value;
      const exists = currentMsgs.some(m => m.message_id === messageId);

      if (!exists) {
        try {
          const loaded = await apiFetch<Message[]>(`/messages/${cleanPhone}/around/${messageId}`);
          if (loaded && loaded.length > 0) {
            messagesSignal.value = loaded;
          }
        } catch (err: any) {
          console.warn('[ContactsStore] Erro ao localizar mensagem de origem:', err);
        }
      }
      highlightedMessageIdSignal.value = messageId;
    }

    activeTabSignal.value = 'tab-messages';

    // Aguardar renderização no DOM e alinhamento de scroll
    await new Promise(resolve => setTimeout(resolve, 400));
  } finally {
    isNavigatingToMessageSignal.value = false;
  }
}

